import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup, FormControl, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { InputfieldsComponent } from '../../components/inputfields/inputfields';
import { NgxTurnstileModule } from 'ngx-turnstile';
import { Selectfields } from "../../components/selectfields/selectfields";
import { Register } from '../../interface/register';
import { InvitationLookupPayload, Registerationservice } from '../../services/registerationservice';
import { CloudFareComponent } from '../../components/cloud-fare/cloud-fare';
import { Loader } from "../../components/loader/loader";
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Toaster } from '../../components/toaster/toaster';
import { finalize, takeUntil, Subject } from 'rxjs';
import { Translate } from '../../services/translate';
import { I18nService } from '../../services/i18n-service';
import { HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'app-registration-identity',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    InputfieldsComponent,
    NgxTurnstileModule,
    Selectfields,
    CloudFareComponent,
    Loader,
    Toaster
  ],
  templateUrl: './registration-identity.html',
  styleUrl: './registration-identity.scss'
})
export class RegistrationIdentity implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private latestSubmitSequence = 0;
  private latestInvitationSequence = 0;
  private pendingInvitationId: string | null = null;
  private pendingInvitationData: any = null;
  private isInvitationModeActive = false;

  constructor(
    private registrationService: Registerationservice,
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public i18n: I18nService
  ) { }
  enableCloudflare = environment.enableCloudflare;
  portalLoginUrl: string = '';
  contactUsUrl: string = environment.contactUsUrl;
  showLoader = false;
  invitationId = '';
  private pendingSubmit = false;
  STRONG_PASSWORD_REGEX = /^(?=.*?\p{Lu})(?=.*?\p{Ll})(?=.*?\d)(?=.*?[^\w\s]|_).{12,}$/u
  captchaToken: string | null = null;
  @ViewChild('toast') toast!: Toaster;
  @ViewChild(CloudFareComponent) cloudFare?: CloudFareComponent;
  salutations: any[] = [
    { label: 'Mr', value: 'MR' },
    { label: 'Mrs', value: 'MRS' },
    { label: 'Ms', value: 'MS' },
    { label: 'Dr', value: 'DR' },
    { label: 'Prof', value: 'PROF' }
  ];

  countries: any[] = [];

  languages: any[] = [];

  currentTranslations: any = {};

  weakPasswordValidator = (control: AbstractControl) => {
    const value = control.value;
    if (!value || typeof value !== 'string') return null;

    // Check for sequential numbers (8 or more digits)
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 8 && this.isSequential(digits)) {
      return { weakPassword: true };
    }

    // Check for repeated characters (8 or more)
    if (/(.)\1{7,}/.test(value)) {
      return { weakPassword: true };
    }

    // Check for common patterns like "test@" followed by numbers
    if (/test@\d+/i.test(value)) {
      return { weakPassword: true };
    }

    // Check for common words followed by patterns
    if (/\b(password|admin|user|login|welcome|hello|world|abc|xyz|qwerty|letmein)\d*@?/i.test(value)) {
      return { weakPassword: true };
    }

    return null;
  };

  private isSequential(str: string): boolean {
    if (str.length < 2) return false;
    for (let i = 1; i < str.length; i++) {
      if (parseInt(str[i]) !== parseInt(str[i - 1]) + 1) return false;
    }
    return true;
  }

  termsLinkClicked = false;
  submitted = false;

  form = new FormGroup(
    {
      name: new FormControl('', [Validators.required, Validators.maxLength(40)]),
      surname: new FormControl('', [Validators.required, Validators.maxLength(40)]),

      salutation: new FormControl<string | null>(null, Validators.required),
      countryOfBirth: new FormControl<string | null>(null, Validators.required),
      language: new FormControl<string | null>(null, Validators.required),

      dob: new FormControl('', [Validators.required]),
      placeOfBirth: new FormControl('', [Validators.required, Validators.maxLength(40), Validators.pattern(/^[a-zA-Z\s']*$/)]),

      email: new FormControl('', [Validators.required, Validators.email]),
      confirmEmail: new FormControl('', [Validators.required]),

      password: new FormControl('', [
        Validators.required,
        Validators.minLength(12),
        Validators.pattern(this.STRONG_PASSWORD_REGEX)
      ]),
      confirmPassword: new FormControl('', Validators.required),

      terms: new FormControl(false, Validators.requiredTrue)
    },
    {
      validators: [
        this.matchFields('email', 'confirmEmail', 'emailMismatch'),
        this.matchFields('password', 'confirmPassword', 'passwordMismatch'),
        this.checkDateField('dob', 'required')
      ],
    }
  );


  /**
   * Handles Ctrl+Z / Cmd+Z undo functionality for text inputs, excluding the date of birth field.
   * @param event The keyboard event triggered by the user.
   * @returns void
   */
  @HostListener('keydown.control.z', ['$event'])
  @HostListener('keydown.meta.z', ['$event'])
  onUndo(event: Event) {
    event.preventDefault();
    const kbEvent = event as KeyboardEvent;
    const tagName = (kbEvent.target as HTMLInputElement).tagName.toLowerCase();
    const controlId = (kbEvent.target as HTMLInputElement).id.replace('control_', '');

    if (tagName !== 'input' || controlId === 'dob')
      return;

    const value = (kbEvent.target as HTMLInputElement).value;

    if (value.length !== 0) {
      (kbEvent.target as HTMLInputElement).value = value.slice(0, -1);
      if ((kbEvent.target as HTMLInputElement).value.length === 0) {
        this.form.get(controlId)?.setValue('', { emitEvent: false });
      } else {
        this.form.get(controlId)?.setValue((kbEvent.target as HTMLInputElement).value, { emitEvent: false });
      }
    }
  }

  matchFields(
    field: string,
    confirmField: string,
    errorKey: string
  ) {
    return (group: AbstractControl) => {
      const main = group.get(field);
      const confirm = group.get(confirmField);

      if (!main || !confirm) return null;
      if (!confirm.value) {
        this.removeError(confirm, errorKey);
        return null;
      }

      if (main.value !== confirm.value) {
        confirm.setErrors({
          ...(confirm.errors || {}),
          [errorKey]: true,
        });
        return { [errorKey]: true };
      }

      this.removeError(confirm, errorKey);
      return null;
    };
  }

  checkDateField(
    field: string,
    errorKey: string
  ) {
    return (group: AbstractControl) => {
      const main = group.get(field);

      if (!main) return null;
      if (main.value) {
        const today = new Date();
        const [mm, dd, yyyy] = main.value.split('/').map(Number);
        const minAgeDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
        const isFeb29 = mm === 2 && dd === 29;

        if (isFeb29 && !this.isLeapYear(yyyy)) {
          main.markAsTouched();
          main.setErrors({
            ...(main.errors || {}),
            ['invalidLeapYearDate']: true,
          });
          return { ['invalidLeapYearDate']: true };
        }

        if (new Date(main.value) > minAgeDate) {
          main.markAsTouched();
          main.setErrors({
            ...(main.errors || {}),
            ["invalidDate"]: true,
          });

          return { ["invalidDate"]: true };
        } else {
          this.removeError(main, "invalidDate");
          return null;
        }
      } else {
        main.setErrors({
          ...(main.errors || {}),
          [errorKey]: true,
        });
        return { [errorKey]: true };
      }
    };
  }


  isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  onTokenReceived(token: string) {
    this.captchaToken = token;

    if (this.pendingInvitationId) {
      const invitationId = this.pendingInvitationId;
      this.pendingInvitationId = null;
      this.fetchInvitationData(invitationId);
    }

    if (this.pendingSubmit) {
      this.pendingSubmit = false;
      this.performSubmit();
    }
  }

  onTokenInvalid() {
    this.captchaToken = null;
    this.pendingSubmit = false;
    this.showLoader = false;
  }


  removeError(control: AbstractControl, key: string) {
    if (!control.errors) return;

    const { [key]: _, ...rest } = control.errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
  }

  ngAfterViewInit(): void {
    this.cloudFare?.refreshToken();
  }

  ngOnInit(): void {
    // Initialize with current language from I18nService
    const currentLang = this.i18n.getCurrentLanguage();
    this.i18n.setLanguage(currentLang);

    this.i18n.translations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(translations => {
        this.currentTranslations = translations;
        this.portalLoginUrl = this.i18n.getLocalizedLoginUrl(environment.portalLoginUrl);
      });

    this.readInvitationParams();
    this.loadMasterData();
  }

  get termsUrl(): string {
    return this.getLocalizedIboUrl('/terms-and-conditions/');
  }

  onTermsLinkClicked(event: MouseEvent): void {
    event.stopPropagation();
    this.termsLinkClicked = true;
    const termsControl = this.form.get('terms');
    if (termsControl?.disabled) {
      termsControl.enable();
    }
  }

  get privacyUrl(): string {
    return this.getLocalizedIboUrl('/terms-and-conditions/privacy-policy/');
  }

  private getLocalizedIboUrl(path: string): string {
    const lang = this.i18n.getCurrentLanguage();
    const prefix = lang === 'en' ? 'https://www.ibo.org' : `https://www.ibo.org/${lang}`;
    return `${prefix}${path}`;
  }

  private readInvitationParams(): void {
    const params = this.activatedRoute.snapshot.queryParams;
    const invitationId = params['inviteid'];
    const isInvitationMode = !!invitationId;

    this.setInvitationMode(isInvitationMode);
    this.populateInvitationFields(params);

    if (invitationId) {
      this.invitationId = invitationId;
      this.showLoader = true;

      this.form.get('confirmEmail')?.clearValidators();
      this.form.get('confirmEmail')?.updateValueAndValidity();

      if (this.captchaToken) {
        this.fetchInvitationData(this.invitationId);
      } else {
        this.pendingInvitationId = this.invitationId;
      }
    }
  }

  private fetchInvitationData(invitationId: string): void {
    if (!this.captchaToken) {
      this.pendingInvitationId = invitationId;
      return;
    }

    this.showLoader = true;
    const invitationSequence = ++this.latestInvitationSequence;

    const payload: InvitationLookupPayload = {
      captchaToken: this.captchaToken ?? '',
      requestId: this.buildInvitationRequestId(),
      invitationId
    };

    this.registrationService.getInvitation(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (invitationSequence !== this.latestInvitationSequence) {
            return;
          }
          this.showLoader = false;
          this.refreshCaptchaAfterInvitationCall();
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          if (this.hasEmptyInvitationRecords(response)) {
            this.toast?.show(this.t('errors.linkNotWorking'), 'error');
            this.invitationId = '';
            this.setInvitationMode(false);
            this.route.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { inviteid: null },
              queryParamsHandling: 'merge'
            });
            return;
          }

          const invitationData = this.extractInvitationData(response);
          this.pendingInvitationData = invitationData;
          this.populateInvitationFields(invitationData);
        },
        error: (err) => {
          // Failed to load invitation data
        }
      });
  }

  private hasEmptyInvitationRecords(response: any): boolean {
    return !!response && Array.isArray(response.records) && response.records.length === 0;
  }

  private refreshCaptchaAfterInvitationCall(): void {
    this.captchaToken = null;
    this.cloudFare?.refreshToken();
  }

  private buildInvitationRequestId(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const suffix = String(Math.floor(Math.random() * 1000000)).padStart(6, '0');
    return `REQ-${yyyy}-${mm}-${dd}-${suffix}`;
  }

  private extractInvitationData(response: any): any {
    if (!response || typeof response !== 'object') {
      return {};
    }

    if (Array.isArray(response.records) && response.records.length > 0) {
      const firstRecord = response.records[0];
      return firstRecord && typeof firstRecord === 'object' ? firstRecord : {};
    }

    if (response.data && typeof response.data === 'object') {
      return response.data;
    }

    if (response.invitation && typeof response.invitation === 'object') {
      return response.invitation;
    }

    return response;
  }

  private populateInvitationFields(source: any): void {
    if (!source || typeof source !== 'object') {
      return;
    }

    this.clearInvitationProvidedFlags();

    const fieldMap: { sourceKeys: string[]; control: string; transform?: (v: string) => string }[] = [
      { sourceKeys: ['firstName', 'name', 'givenName', 'FirstName'], control: 'name' },
      { sourceKeys: ['lastName', 'surname', 'familyName', 'LastName'], control: 'surname' },
      {
        sourceKeys: ['salutation', 'Salutation'],
        control: 'salutation',
        transform: (v: string) => {
          const normalized = String(v).replace('.', '').trim().toLowerCase();
          const match = this.salutations.find(s =>
            s.label.toLowerCase() === normalized || s.value.toLowerCase() === normalized
          );
          return match ? match.value : v;
        }
      },
      { sourceKeys: ['email', 'Email'], control: 'email' },
      {
        sourceKeys: ['countryOfBirth', 'Country_of_Birth__c'],
        control: 'countryOfBirth',
        transform: (v: string) => {
          const match = this.countries.find(
            c => c.value === v || c.label.toLowerCase() === v.toLowerCase()
          );
          return match ? match.value : v;
        }
      },
      { sourceKeys: ['language', 'preferredLanguage'], control: 'language' },
      { sourceKeys: ['placeOfBirth', 'Birth_Locale__c'], control: 'placeOfBirth' },
      {
        sourceKeys: ['dateOfBirth', 'dob', 'Birthdate'],
        control: 'dob',
        // API / URL uses YYYY-MM-DD; the form date picker expects DD/MM/YYYY
        transform: (v: string) => {
          const match = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          return match ? `${match[3]}/${match[2]}/${match[1]}` : v;
        }
      }
    ];

    fieldMap.forEach(({ sourceKeys, control, transform }) => {
      const value = this.getFirstInvitationValue(source, sourceKeys);
      if (value != null && value !== '') {
        const formControl = this.form.get(control);
        if (formControl) {
          formControl.setValue(transform ? transform(value) : value);
          (formControl as any).__invitationProvided = true;
        }
      }
    });

    const invitationIdFromPayload = this.getFirstInvitationValue(source, ['invitationId', 'Invitation_ID__c']);
    if (invitationIdFromPayload) {
      this.invitationId = invitationIdFromPayload;
    }

    this.setInvitationMode(this.isInvitationModeActive);
  }

  private clearInvitationProvidedFlags(): void {
    const controlsToClear = [
      'name',
      'surname',
      'salutation',
      'countryOfBirth',
      'language',
      'dob',
      'placeOfBirth',
      'email',
      'confirmEmail'
    ];

    controlsToClear.forEach(controlName => {
      const control = this.form.get(controlName);
      if (control) {
        delete (control as any).__invitationProvided;
      }
    });
  }

  private setInvitationMode(isInvitationMode: boolean): void {
    this.isInvitationModeActive = isInvitationMode;
    const controlsToToggle = [
      'name',
      'surname',
      'salutation',
      'countryOfBirth',
      'language',
      'dob',
      'placeOfBirth',
      'email',
    ];

    controlsToToggle.forEach(controlName => {
      const control = this.form.get(controlName);
      if (!control) return;

      const provided = (control as any).__invitationProvided === true;
      if (isInvitationMode && provided) {
        control.disable({ emitEvent: false });
      } else if (!isInvitationMode) {
        control.enable({ emitEvent: false });
        delete (control as any).__invitationProvided;
      } else {
        control.enable({ emitEvent: false });
      }
    });

    const confirmEmailControl = this.form.get('confirmEmail');
    const emailControl = this.form.get('email');
    if (isInvitationMode && emailControl && (emailControl as any).__invitationProvided) {
      confirmEmailControl?.disable({ emitEvent: false });
      if (confirmEmailControl && !confirmEmailControl.value) {
        confirmEmailControl.setValue(emailControl.value, { emitEvent: false });
      }
    } else if (!isInvitationMode) {
      confirmEmailControl?.enable({ emitEvent: false });
    } else {
      confirmEmailControl?.enable({ emitEvent: false });
    }
  }

  private getFirstInvitationValue(source: any, keys: string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (value != null && value !== '') {
        return String(value);
      }
    }

    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMasterData(): void {
    this.registrationService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.countries = this.extractCountries(data);
          this.languages = this.extractLanguages(data);
          if (this.pendingInvitationData) {
            this.populateInvitationFields(this.pendingInvitationData);
            this.pendingInvitationData = null;
          }
          // Trigger change detection to ensure dropdowns update with loaded data
          this.cdr.markForCheck();
        },
        error: (err) => {
          // Failed to load master data
        }
      });
  }

  private extractCountries(data: any) {
    return data.regions
      .flatMap((r: any) => r.subRegions)
      .flatMap((sr: any) => sr.countries)
      .map((c: any) => {
        const enName = c.names.find((n: any) => n.language === 'en-GB');
        const fallbackName = c.names[0];
        const label =
          enName?.displayText ||
          enName?.text ||
          fallbackName?.displayText ||
          fallbackName?.text ||
          c.id;
        return { value: c.id, label };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }


  private extractLanguages(data: any) {
    const languageSet = new Set<string>();

    data.regions
      .flatMap((r: any) => r.subRegions)
      .flatMap((sr: any) => sr.countries)
      .flatMap((c: any) => c.names)
      .forEach((n: any) => languageSet.add(n.language));

    return Array.from(languageSet)
      .filter(code => code.split('-')[0].toLowerCase() !== 'ja')
      .map(code => ({
        value: code,
        label: this.getNativeLanguageName(code)
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }


  private getNativeLanguageName(code: string): string {
    try {
      const lang = code.split('-')[0]; // en-GB → en
      const display = new Intl.DisplayNames([lang], { type: 'language' });
      if (display) {
        const str = display.of(lang);
        if (str) {
          return str.charAt(0).toUpperCase() + str.slice(1);
        }
      }
      return code;
    } catch {
      return code;
    }
  }




  private buildPayload(): Register {
    // getRawValue() includes disabled controls (pre-filled from invitation link)
    const raw = this.form.getRawValue();
    const payload: Register = {
      requestId: `REQ-${Date.now()}`,
      firstName: raw.name!,
      lastName: raw.surname!,
      salutation: raw.salutation!,
      preferredLanguage: raw.language!,
      email: raw.email!,
      dateOfBirth: this.dateFormat(raw.dob!), // YYYY-MM-DD
      countryOfBirth: raw.countryOfBirth!,
      placeOfBirth: raw.placeOfBirth!,
      invitationId: this.invitationId,
      password: raw.password!,
      captchaToken: this.captchaToken!
    };

    return payload;
  }

  private dateFormat(dates: string) {
    const [dd = '', mm = '', yyyy = ''] = dates.split('/');
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }

  private t(path: string, params?: Record<string, string | number>): string {
    return this.i18n.t(this.currentTranslations, path, params);
  }

  private getRegistrationErrorMessage(err: unknown): string {
    const fallbackMessage = this.t('errors.genericServerError');

    if (!(err instanceof HttpErrorResponse)) {
      return fallbackMessage;
    }

    const payload = err.error;

    const messageFromPayload = this.extractErrorMessage(payload);
    if (messageFromPayload) {
      if (this.isInvitationAlreadyRegistered(messageFromPayload)) {
        return this.t('errors.linkAlreadyUsed');
      }
      if (this.isEmailAlreadyRegistered(messageFromPayload)) {
        return this.t('errors.emailAlreadyUsed');
      }
      if (this.isFieldLengthExceeded(messageFromPayload)) {
        return this.t('errors.nameTooLong');
      }
      return messageFromPayload;
    }

    if (err.status === 400) {
      return this.t('errors.invalidInformation');
    }

    if (err.status === 401 || err.status === 403) {
      return this.t('errors.sessionVerificationFailed');
    }

    if (err.status === 409) {
      return this.t('errors.emailAlreadyUsed');
    }

    if (err.status === 429) {
      return this.t('errors.tooManyAttempts');
    }

    if (err.status >= 500) {
      return this.t('errors.registrationUnavailable');
    }

    if (err.status === 0) {
      return this.t('errors.offline');
    }

    return fallbackMessage;
  }

  private extractErrorMessage(payload: any): string | null {
    if (!payload) {
      return null;
    }

    if (typeof payload === 'string') {
      const text = payload.trim();
      // Check for JSON conversion errors related to date
      if (this.isJsonConversionError(text)) {
        return this.t('errors.invalidDateFormat');
      }
      return text ? text : null;
    }

    if (Array.isArray(payload) && payload.length > 0) {
      const first = payload[0];
      if (typeof first === 'string') {
        const text = first.trim();
        if (this.isJsonConversionError(text)) {
          return this.t('errors.invalidDateFormat');
        }
        return text ? text : null;
      }
    }

    if (typeof payload?.message === 'string' && payload.message.trim()) {
      const message = payload.message.trim();
      if (this.isJsonConversionError(message)) {
        return this.t('errors.invalidDateFormat');
      }
      return message;
    }

    if (typeof payload?.detail === 'string' && payload.detail.trim()) {
      const detail = payload.detail.trim();
      if (this.isJsonConversionError(detail)) {
        return this.t('errors.invalidDateFormat');
      }
      return detail;
    }

    if (typeof payload?.title === 'string' && payload.title.trim()) {
      const title = payload.title.trim();
      if (this.isJsonConversionError(title)) {
        return this.t('errors.invalidDateFormat');
      }
      return title;
    }

    const validationErrors = payload?.errors;
    if (validationErrors && typeof validationErrors === 'object') {
      const fieldErrors = Object.values(validationErrors)
        .flatMap((value: any) => Array.isArray(value) ? value : [value])
        .map((item: any) => {
          if (typeof item === 'string') {
            const text = item.trim();
            if (this.isJsonConversionError(text)) {
              return this.t('errors.invalidDateFormat');
            }
            return text;
          }
          return '';
        })
        .filter(Boolean);

      if (fieldErrors.length > 0) {
        return fieldErrors[0];
      }
    }

    return null;
  }

  private isJsonConversionError(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    return lowerMessage.includes('json') &&
      (lowerMessage.includes('conversion') ||
        lowerMessage.includes('parse') ||
        lowerMessage.includes('deserial') ||
        lowerMessage.includes('dateofbirth') ||
        lowerMessage.includes('date of birth') ||
        lowerMessage.includes('invalid format'));
  }

  private isEmailAlreadyRegistered(message: string): boolean {
    const normalizedMessage = message.toLowerCase();
    return (
      (normalizedMessage.includes('email') && normalizedMessage.includes('exist')) ||
      normalizedMessage.includes('already registered') ||
      normalizedMessage.includes('already in use') ||
      normalizedMessage.includes('duplicate')
    );
  }

  private isInvitationAlreadyRegistered(message: string): boolean {
    const normalizedMessage = message.toLowerCase();
    return (
      normalizedMessage.includes('invalid invitation data') ||
      (normalizedMessage.includes('invitation') && normalizedMessage.includes('already registered')) ||
      (normalizedMessage.includes('invitation') && normalizedMessage.includes('already used'))
    );
  }

  private isFieldLengthExceeded(message: string): boolean {
    const m = message.toLowerCase();
    return (
      (m.includes('character') && (m.includes('limit') || m.includes('exceed') || m.includes('maximum') || m.includes('long'))) ||
      (m.includes('too long')) ||
      (m.includes('max') && m.includes('length'))
    );
  }


  submit() {
    if (this.showLoader) {
      return;
    }

    this.toast.clear();
    this.submitted = true;
    this.form.markAllAsTouched();
    this.form.get('terms')?.markAsTouched();

    // Check for weak password
    const weakError = this.weakPasswordValidator(this.form.controls.password);
    if (weakError) {
      this.toast.show(this.t('errors.weakPassword'), 'error');
      return;
    }

    const { terms, ...otherControls } = this.form.controls;

    // Explicitly validate DOB field to catch any format issues before submission
    const dobControl = this.form.get('dob');
    if (dobControl) {
      dobControl.markAsTouched();
      const dobValue = typeof dobControl.value === 'string' ? dobControl.value.trim() : '';

      if (!dobValue) {
        dobControl.setErrors({ ...(dobControl.errors || {}), required: true });
      } else {
        const [mm = '', dd = '', yyyy = ''] = dobValue.split('/').map((c: string) => c.trim());
        if (!mm || !dd || !yyyy || !/^\d{2}$/.test(mm) || !/^\d{2}$/.test(dd) || !/^\d{4}$/.test(yyyy)) {
          dobControl.setErrors({ ...(dobControl.errors || {}), invalidDate: true });
        } else {
          const mmNum = Number(mm);
          const ddNum = Number(dd);
          const yyyyNum = Number(yyyy);

          const parsedDate = new Date(yyyyNum, mmNum - 1, ddNum);
          const isExactDate =
            parsedDate.getFullYear() === yyyyNum &&
            parsedDate.getMonth() === mmNum - 1 &&
            parsedDate.getDate() === ddNum;

          if (mmNum === 2 && ddNum === 29 && !this.isLeapYear(yyyyNum)) {
            dobControl.setErrors({ ...(dobControl.errors || {}), invalidLeapYearDate: true });
          } else if (!isExactDate) {
            dobControl.setErrors({ ...(dobControl.errors || {}), invalidDate: true });
          }
        }
      }
    }

    const otherFieldsInvalid = Object.values(otherControls)
      .some(control => control.invalid);

    if (otherFieldsInvalid) {
      this.toast.show(
        this.t('errors.requiredFieldsInvalid'),
        'error'
      );
      return;
    }


    if (!this.form.controls['terms'].value) {
      return;
    }

    this.performSubmit();
  }

  private performSubmit() {
    if (!this.captchaToken) {
      // Token not yet ready — defer submit until new token arrives
      this.pendingSubmit = true;
      this.showLoader = true;
      this.cloudFare?.refreshToken();
      return;
    }

    this.showLoader = true;
    const submitSequence = ++this.latestSubmitSequence;

    const payload = this.buildPayload();
    const endPoint = '/registration';

    this.registrationService.createUser(endPoint, payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (submitSequence !== this.latestSubmitSequence) {
            return;
          }
          this.showLoader = false;
          this.captchaToken = null;
          this.pendingSubmit = false;
          this.cloudFare?.refreshToken();
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          if (submitSequence !== this.latestSubmitSequence) {
            return;
          }
          this.route.navigate(['/success']);
        },
        error: (err) => {
          if (submitSequence !== this.latestSubmitSequence) {
            return;
          }
          this.toast.show(this.getRegistrationErrorMessage(err), 'error');
        }
      });
  }

}
